/**
 * ARGOV Connect — Schedule Meeting
 * ARGOV-EVENTS-CONNECT-01 | Phase 1
 *
 * Creates a new meeting with: provider, URL, schedule, context links, attendees.
 * No automatic external invitations. Invitation draft is generated for manual sharing.
 * Capability required: meeting.schedule
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Trash2, Video } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = [
  { value: "GOOGLE_MEET", label: "Google Meet" },
  { value: "ZOOM",        label: "Zoom" },
  { value: "MS_TEAMS",    label: "Microsoft Teams" },
  { value: "WEBEX",       label: "Cisco Webex" },
  { value: "OTHER",       label: "Other HTTPS link" },
];

const MEETING_TYPES = [
  { value: "CONSULTATION",        label: "Consultation" },
  { value: "FOLLOW_UP",           label: "Follow-up" },
  { value: "INTERNAL",            label: "Internal" },
  { value: "VENUE_WALKTHROUGH",   label: "Venue Walkthrough" },
  { value: "SUPPLIER_NEGOTIATION",label: "Supplier Negotiation" },
  { value: "ONBOARDING",          label: "Onboarding" },
  { value: "REVIEW",              label: "Review" },
  { value: "OTHER",               label: "Other" },
];

const ENTITY_TYPES = [
  { value: "LEAD",         label: "CRM Lead" },
  { value: "OPPORTUNITY",  label: "Opportunity" },
  { value: "CUSTOMER",     label: "Customer" },
  { value: "EVENT",        label: "Event" },
  { value: "EVENT_THREAD", label: "Event Thread" },
];

const TIMEZONES = [
  "Asia/Kolkata", "UTC", "America/New_York", "America/Los_Angeles",
  "Europe/London", "Europe/Paris", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney",
];

function emptyAttendee() {
  return { name: "", email: "", role: "REQUIRED" };
}

function emptyRef() {
  return { entity_type: "LEAD", entity_id: "", entity_label: "" };
}

export default function ConnectScheduleMeeting() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title:            "",
    description:      "",
    meeting_type:     "CONSULTATION",
    provider:         "GOOGLE_MEET",
    meeting_url:      "",
    scheduled_at:     "",
    duration_minutes: 30,
    timezone:         "Asia/Kolkata",
  });

  const [attendees,    setAttendees]    = useState([]);
  const [contextRefs,  setContextRefs]  = useState([]);
  const [urlError,     setUrlError]     = useState("");

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateUrl = (url) => {
    if (!url) { setUrlError("Meeting URL is required"); return false; }
    if (!url.startsWith("https://")) { setUrlError("URL must start with https://"); return false; }
    if (url.includes(" ")) { setUrlError("URL must not contain spaces"); return false; }
    setUrlError("");
    return true;
  };

  const addAttendee    = () => setAttendees(a => [...a, emptyAttendee()]);
  const removeAttendee = i => setAttendees(a => a.filter((_, idx) => idx !== i));
  const setAttendee    = (i, k, v) => setAttendees(a => a.map((at, idx) => idx === i ? { ...at, [k]: v } : at));

  const addRef    = () => setContextRefs(r => [...r, emptyRef()]);
  const removeRef = i => setContextRefs(r => r.filter((_, idx) => idx !== i));
  const setRef    = (i, k, v) => setContextRefs(r => r.map((rf, idx) => idx === i ? { ...rf, [k]: v } : rf));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim())        return toast.error("Meeting title is required");
    if (!validateUrl(form.meeting_url)) return;
    if (!form.scheduled_at)        return toast.error("Schedule date/time is required");

    // Validate context refs minimal requirements
    for (const ref of contextRefs) {
      if (!ref.entity_id.trim() || !ref.entity_label.trim()) {
        toast.error("Each context link requires an ID and label");
        return;
      }
    }

    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      attendees:    attendees.filter(a => a.name.trim()),
      context_refs: contextRefs,
    };

    setLoading(true);
    try {
      const r = await fetch(`${API}/api/connect/meetings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail?.message || err?.detail || `HTTP ${r.status}`);
      }
      const created = await r.json();
      toast.success(`Meeting scheduled: ${created.meeting_ref}`);
      navigate(`/connect/meetings/${created.id}`);
    } catch (e) {
      toast.error(`Failed to schedule meeting: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5 p-1" data-testid="connect-schedule-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          data-testid="connect-schedule-back-btn"
          onClick={() => navigate("/connect/meetings")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Schedule Meeting</h1>
          <p className="text-xs text-slate-500">ARGOV Connect — Phase 1 · Manual link entry</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Core details */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Video className="w-4 h-4 text-indigo-500" /> Meeting Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-600">Title *</Label>
              <Input
                data-testid="connect-form-title"
                placeholder="e.g. Initial Consultation — Priya Kapoor Wedding"
                value={form.title}
                onChange={e => setField("title", e.target.value)}
                required
                className="mt-1 h-9"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Meeting Type</Label>
                <Select value={form.meeting_type} onValueChange={v => setField("meeting_type", v)}>
                  <SelectTrigger data-testid="connect-form-type" className="mt-1 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEETING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Provider</Label>
                <Select value={form.provider} onValueChange={v => setField("provider", v)}>
                  <SelectTrigger data-testid="connect-form-provider" className="mt-1 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">Meeting URL * (HTTPS)</Label>
              <Input
                data-testid="connect-form-url"
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                value={form.meeting_url}
                onChange={e => { setField("meeting_url", e.target.value); validateUrl(e.target.value); }}
                required
                className={`mt-1 h-9 font-mono text-xs ${urlError ? "border-red-400" : ""}`}
              />
              {urlError && <p className="text-xs text-red-500 mt-1">{urlError}</p>}
              <p className="text-xs text-slate-400 mt-1">
                Create the meeting in Google Meet / Zoom, then paste the join link here.
                ARGOV Connect stores the link — it does not create meetings in external providers.
              </p>
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">Description</Label>
              <Textarea
                data-testid="connect-form-description"
                placeholder="Agenda, context or preparation notes…"
                value={form.description}
                onChange={e => setField("description", e.target.value)}
                className="mt-1 text-sm resize-none"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Date & Time *</Label>
                <Input
                  data-testid="connect-form-datetime"
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={e => setField("scheduled_at", e.target.value)}
                  required
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Duration (minutes)</Label>
                <Select value={String(form.duration_minutes)} onValueChange={v => setField("duration_minutes", Number(v))}>
                  <SelectTrigger data-testid="connect-form-duration" className="mt-1 h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[15,30,45,60,90,120,180,240].map(d => (
                      <SelectItem key={d} value={String(d)}>{d} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Timezone</Label>
              <Select value={form.timezone} onValueChange={v => setField("timezone", v)}>
                <SelectTrigger data-testid="connect-form-timezone" className="mt-1 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Context Links (Phase 1: CRM + Events) */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Context Links</CardTitle>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="connect-add-context-btn"
                onClick={addRef}
                className="gap-1 h-7 text-xs"
              >
                <Plus className="w-3 h-3" /> Add
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Link to CRM Lead, Opportunity, Customer, Event or Event Thread.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {contextRefs.length === 0 && (
              <p className="text-xs text-slate-400 italic">No context links added.</p>
            )}
            {contextRefs.map((ref, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`connect-context-ref-${i}`}>
                <Select value={ref.entity_type} onValueChange={v => setRef(i, "entity_type", v)}>
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map(et => <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Record ID"
                  value={ref.entity_id}
                  onChange={e => setRef(i, "entity_id", e.target.value)}
                  className="h-8 text-xs font-mono flex-1"
                />
                <Input
                  placeholder="Display label"
                  value={ref.entity_label}
                  onChange={e => setRef(i, "entity_label", e.target.value)}
                  className="h-8 text-xs flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => removeRef(i)}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Attendees */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Attendees</CardTitle>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="connect-add-attendee-btn"
                onClick={addAttendee}
                className="gap-1 h-7 text-xs"
              >
                <Plus className="w-3 h-3" /> Add Attendee
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {attendees.length === 0 && (
              <p className="text-xs text-slate-400 italic">No attendees added. You will be the host.</p>
            )}
            {attendees.map((a, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`connect-attendee-row-${i}`}>
                <Input
                  placeholder="Full name *"
                  value={a.name}
                  onChange={e => setAttendee(i, "name", e.target.value)}
                  className="h-8 text-xs flex-1"
                />
                <Input
                  placeholder="Email (optional)"
                  value={a.email}
                  onChange={e => setAttendee(i, "email", e.target.value)}
                  className="h-8 text-xs flex-1"
                  type="email"
                />
                <Select value={a.role} onValueChange={v => setAttendee(i, "role", v)}>
                  <SelectTrigger className="w-28 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REQUIRED">Required</SelectItem>
                    <SelectItem value="OPTIONAL">Optional</SelectItem>
                    <SelectItem value="ORGANIZER">Organizer</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => removeAttendee(i)}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-3 justify-end pb-6">
          <Button
            type="button"
            variant="outline"
            data-testid="connect-schedule-cancel-btn"
            onClick={() => navigate("/connect/meetings")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="connect-schedule-submit-btn"
            disabled={loading}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Video className="w-4 h-4" />
            {loading ? "Scheduling…" : "Schedule Meeting"}
          </Button>
        </div>
      </form>
    </div>
  );
}
